import React from 'react';

// Tier 2 / F-18 — Per-thread composer draft persistence.
//
// Stores the typing-in-progress text for each WhatsApp thread in
// localStorage so that switching threads (or refreshing the page)
// does not lose the operator's in-flight reply.
//
// Storage key:  wa-draft-${threadId}
//
// Save is debounced (300ms) so we don't thrash localStorage on every
// keystroke. Reads are synchronous on mount (and whenever threadId
// changes) so the composer hydrates before the first paint.
//
// Bonus: external callers (e.g. the conversation list in
// Contacts.jsx) can read the same key off localStorage at render
// time to surface a "Draft:" prefix without going through React
// state subscriptions.

const KEY_PREFIX = 'wa-draft-';
const DEBOUNCE_MS = 300;

const buildKey = (threadId) => (threadId == null ? null : `${KEY_PREFIX}${threadId}`);

// Safe localStorage read — wrapped because some sandboxed iframes
// (or private-browsing modes on Safari) throw on access.
const readLocal = (key) => {
    if (!key) return '';
    try {
        return window.localStorage.getItem(key) || '';
    } catch (_) {
        return '';
    }
};

const writeLocal = (key, value) => {
    if (!key) return;
    try {
        if (value && value.length > 0) {
            window.localStorage.setItem(key, value);
        } else {
            window.localStorage.removeItem(key);
        }
    } catch (_) {
        /* noop — storage full / disabled. Drafts are best-effort. */
    }
};

// ---------------------------------------------------------------
// Public helper used by the contact list to peek at a draft for
// a thread WITHOUT subscribing to it. Returns the trimmed string.
// ---------------------------------------------------------------
export const readDraftForThread = (threadId) => readLocal(buildKey(threadId));

// ---------------------------------------------------------------
// Hook
// ---------------------------------------------------------------
export default function useThreadDraft(threadId) {
    const key = buildKey(threadId);
    // Hydrate synchronously from localStorage so the textarea
    // never flashes empty.
    const [draft, setDraftState] = React.useState(() => readLocal(key));

    // Re-hydrate when the thread changes.
    React.useEffect(() => {
        setDraftState(readLocal(key));
    }, [key]);

    // Persist with debounce. The most-recent value is captured in
    // a ref so we always flush the latest text on flush/unmount.
    const pendingValueRef = React.useRef(draft);
    const timerRef = React.useRef(null);

    const flush = React.useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        writeLocal(key, pendingValueRef.current || '');
    }, [key]);

    const setDraft = React.useCallback((value) => {
        const next = value == null ? '' : String(value);
        pendingValueRef.current = next;
        setDraftState(next);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            writeLocal(key, pendingValueRef.current || '');
            timerRef.current = null;
        }, DEBOUNCE_MS);
    }, [key]);

    const clearDraft = React.useCallback(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        pendingValueRef.current = '';
        setDraftState('');
        writeLocal(key, '');
    }, [key]);

    // Flush on unmount AND when the thread id rotates (so a draft
    // for thread A is committed before we hydrate thread B).
    React.useEffect(() => () => flush(), [flush]);

    return { draft, setDraft, clearDraft };
}
